import { useState } from 'react';
import { Range, RangeKeyDict } from 'react-date-range';

import { FontSize, FontWeight } from 'commons';
import { Button, DateRangePicker, Header, Main, Text } from 'components';
import {
  addDays,
  addYears,
  endOfDay,
  endOfToday,
  startOfDay,
  subYears,
} from 'date-fns';
import { useAuth, useGenerateReport } from 'hooks';
import { reportsTransactionsService } from 'services';
import useSWR from 'swr';

import {
  EmptyResult,
  GeneralValueCards,
  ReportEmpty,
  ReportError,
  ReportInfo,
  ReportLoader,
} from './components';

const DEFAULT_PAST_SELECTION = -30;

const PAST_YEARS_LIMIT = 5;
const FUTURE_YEARS_LIMIT = 1;

const MIN_DATE = subYears(new Date(), PAST_YEARS_LIMIT);
const MAX_DATE = addYears(new Date(), FUTURE_YEARS_LIMIT);

export function FinancialTransactionsPage() {
  const { selectedStore } = useAuth();
  const { generateReport, generateLoading, reportStatus, reportStatusError } =
    useGenerateReport();
  const [dateRangeState, setDateRangeState] = useState<Range>({
    startDate: startOfDay(addDays(new Date(), DEFAULT_PAST_SELECTION)),
    endDate: endOfDay(new Date()),
  });
  const store = selectedStore.name || '';

  function changeDateRange(dateRange: RangeKeyDict) {
    const { startDate, endDate } = dateRange.range1;

    if (!startDate || !endDate) return;

    setDateRangeState({
      startDate: startOfDay(startDate),
      endDate: endOfDay(endDate),
    });
  }

  function handleReset() {
    setDateRangeState({
      startDate: startOfDay(addDays(new Date(), DEFAULT_PAST_SELECTION)),
      endDate: endOfToday(),
    });
  }

  const { data: balance, isValidating: isValidatingBalance } = useSWR(
    `financial-transaction-balance-${store}`,
    () => reportsTransactionsService.getBalance([store], 'BASIC'),
    { revalidateOnFocus: false }
  );

  async function handleGenerateReport() {
    await generateReport({
      startDate: dateRangeState.startDate!,
      endDate: dateRangeState.endDate!,
    });
  }

  return (
    <Main>
      <Header />

      <div
        className="h-full flex flex-col gap-6 px-12 py-8 overflow-y-auto overflow-x-hidden
          tablet:px-7"
      >
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Text>
            Acompanhe a evolução das transações financeiras até o momento.
          </Text>
        </div>

        <GeneralValueCards
          balance={balance}
          isValidating={isValidatingBalance && !balance}
        />

        <div className="flex flex-col gap-2">
          <Text size={FontSize.font16} weight={FontWeight.bold}>
            Gerar um relatório
          </Text>
          <div className="flex justify-between items-center gap-2 w-full">
            <Text>
              Gere relatórios para ver todas movimentações em determinado
              período
            </Text>
            <div className="flex items-center gap-4">
              <div className="flex flex-wrap items-center gap-2">
                <DateRangePicker
                  values={dateRangeState}
                  onChangeRange={changeDateRange}
                  onReset={handleReset}
                  minimumDate={MIN_DATE}
                  maximumDate={MAX_DATE}
                  maxPeriod={0}
                />
              </div>
              <Button
                disabled={generateLoading}
                onClick={generateLoading ? undefined : handleGenerateReport}
              >
                Gerar novo relatório
              </Button>
            </div>
          </div>
        </div>

        {reportStatusError && <ReportError />}

        {!reportStatusError &&
          (!reportStatus || reportStatus.status === 'NOT_FOUND') && (
            <ReportEmpty />
          )}

        {!reportStatusError && reportStatus?.status === 'IN_PROGRESS' && (
          <ReportLoader dateRange={dateRangeState} />
        )}

        {!reportStatusError && reportStatus?.status === 'NO_DATA_FOUND' && (
          <EmptyResult dateRange={dateRangeState} />
        )}

        {!reportStatusError && reportStatus?.status === 'DONE' && (
          <ReportInfo
            dateRange={{
              startDate: new Date(reportStatus.startDate),
              endDate: new Date(reportStatus.endDate),
            }}
          />
        )}

        {!reportStatusError && reportStatus?.status === 'ERROR' && (
          <ReportError />
        )}
      </div>
    </Main>
  );
}
